/**
 * 72. Payroll Runs — payroll period; gross/net; verification;
 * approval; payment.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatMoney } from "@/lib/money";

interface PayRunRow {
  id: string;
  runNumber: string;
  period: string;
  payDate: string | null;
  employeeCount: number;
  grossTotal: string;
  netTotal: string;
  currency: string;
  status: "Draft" | "Verifying" | "Approved" | "Posted" | "Cancelled";
}

export function PayrollRunsPage() {
  const { data, isLoading } = useApiList<PayRunRow>(
    ["payroll", "pay-runs"],
    "/payroll/pay-runs",
  );

  return (
    <div>
      <PageHeader
        title="Payroll Runs"
        subtitle="Payroll period, gross/net totals, verification, approval, and payment."
        actions={<Button variant="primary">New pay run</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Pay runs (${data.total})`}>
          <DataTable<PayRunRow>
            rows={data.items}
            columns={[
              {
                key: "runNumber",
                label: "Run",
                render: (r) => (
                  <span className="atlas-mono">{r.runNumber}</span>
                ),
              },
              { key: "period", label: "Period" },
              {
                key: "payDate",
                label: "Pay date",
                render: (r) => (r.payDate ? formatDate(r.payDate) : "—"),
              },
              { key: "employeeCount", label: "Employees", numeric: true },
              {
                key: "grossTotal",
                label: "Gross",
                numeric: true,
                render: (r) => formatMoney(r.grossTotal, r.currency),
              },
              {
                key: "netTotal",
                label: "Net",
                numeric: true,
                render: (r) => formatMoney(r.netTotal, r.currency),
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Posted" || r.status === "Approved" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Cancelled" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : (
                    <Badge tone="warning">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
